import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import recruiterApiService from "../../core/recruiterApiService";
import Pagination from "../../shared/Pagination";
import { formatDate, formatIstDateTime } from "../../shared/dateFormat";
import "../jobPosting/JobPostings.css";

const STATUS_PILL = {
  NEW: "status-pill-secondary",
  L1_PENDING: "status-pill-warning",
  L2_PENDING: "status-pill-warning",
  APPROVED: "status-pill-success",
  L1_REJECTED: "status-pill-danger",
  L2_REJECTED: "status-pill-danger",
  FULFILLED: "status-pill-info",
};

// Statuses an approver can ever see, per level - drives the status filter dropdown.
const L1_STATUS_OPTIONS = ["L1_PENDING", "L2_PENDING", "APPROVED", "L1_REJECTED", "L2_REJECTED"];
const L2_STATUS_OPTIONS = ["L2_PENDING", "APPROVED", "L2_REJECTED"];

/** SCL_53: BOB-style "09-09-2026 12.45pm" in IST. */
const formatApprovalDateTime = formatIstDateTime;

const Approvals = () => {
  const navigate = useNavigate();
  const privileges = useSelector((state) => state.user.privileges) || {};
  const isL2 = !!privileges.L2Approval;
  const isL1 = !!privileges.L1Approval;
  const level = isL2 ? "L2" : "L1";

  const [requisitions, setRequisitions] = useState([]);
  const [positionsByReq, setPositionsByReq] = useState({});
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState([]);
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [historyModal, setHistoryModal] = useState({ show: false, requisition: null, rows: [], loading: false });
  const actingRef = useRef(false);

  const openApprovalHistory = async (req, e) => {
    e.stopPropagation();
    setHistoryModal({ show: true, requisition: req, rows: [], loading: true });
    try {
      const res = await recruiterApiService.getRequisitionApprovalHistory(req.id);
      setHistoryModal({ show: true, requisition: req, rows: res.data.data || [], loading: false });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load approval history");
      setHistoryModal({ show: true, requisition: req, rows: [], loading: false });
    }
  };

  const closeApprovalHistory = () => setHistoryModal({ show: false, requisition: null, rows: [], loading: false });

  // Guards against overlapping fetches (e.g. React StrictMode's dev-only double-invoke on mount).
  const loadInFlightRef = useRef(false);
  const load = async (pageArg) => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoading(true);
    try {
      const params = { search: searchText || undefined, status: statusFilter || undefined, page: pageArg ?? page, size };
      const res = isL2 ? await recruiterApiService.getL2Requisitions(params) : await recruiterApiService.getL1Requisitions(params);
      const content = res.data.data.content || [];
      setRequisitions(content);
      setTotalPages(res.data.data.totalPages || 0);

      // SCL_20: position details were not shown under a requisition in the approver's view.
      const positionResults = await Promise.all(
        content.map((req) =>
          recruiterApiService.getPositionsByRequisition(req.id).then((r) => [req.id, r.data.data]).catch(() => [req.id, []])
        )
      );
      const map = {};
      positionResults.forEach(([id, positions]) => { map[id] = positions; });
      setPositionsByReq(map);
    } catch (e) {
      toast.error("Failed to load requisitions for approval");
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  };

  const toggleExpand = (requisitionId) => {
    setExpanded((prev) => ({ ...prev, [requisitionId]: !prev[requisitionId] }));
  };

  useEffect(() => {
    setPage(0);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isL2]);

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  // Status filter reloads immediately (and jumps back to page 1) on a genuine change - not on mount.
  const prevStatusFilterRef = useRef(statusFilter);
  useEffect(() => {
    if (prevStatusFilterRef.current === statusFilter) return;
    prevStatusFilterRef.current = statusFilter;
    setPage(0);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Search is performed server-side - only reload (and jump back to page 1) on a genuine
  // searchText change, not on mount.
  const prevSearchTextRef = useRef(searchText);
  useEffect(() => {
    if (prevSearchTextRef.current === searchText) return;
    prevSearchTextRef.current = searchText;
    const timeout = setTimeout(() => {
      setPage(0);
      load(0);
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const actOnSelected = async (approve) => {
    if (selected.length === 0) return;
    if (actingRef.current) return;
    actingRef.current = true;
    setActing(true);
    try {
      await recruiterApiService.approveOrReject({ requisitionIds: selected, approve, comments });
      toast.success(approve ? "Requisition(s) approved successfully" : "Requisition(s) rejected successfully");
      setSelected([]);
      setComments("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || (approve ? "Failed to approve requisitions" : "Failed to reject requisitions"));
    } finally {
      actingRef.current = false;
      setActing(false);
    }
  };

  const pendingStatus = isL2 ? "L2_PENDING" : "L1_PENDING";
  const statusOptions = isL2 ? L2_STATUS_OPTIONS : L1_STATUS_OPTIONS;

  return (
    <div className="job-postings-page">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="list-card-title-wrap">
          <i className="bi bi-check2-square" />
          <span className="list-card-title">Requisition Requests</span>
          <span className="list-card-count">({level} Approver)</span>
        </div>
        {selected.length > 0 && (
          <div className="d-flex gap-2 align-items-center">
            <input
              className="form-control"
              placeholder="Comments (optional)"
              style={{ width: 260 }}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
            <button className="btn btn-outline-danger" disabled={acting} onClick={() => actOnSelected(false)}>Reject</button>
            <button className="btn btn-outline-success" disabled={acting} onClick={() => actOnSelected(true)}>Approve</button>
          </div>
        )}
      </div>

      <div className="row filters-row g-2 mb-3 align-items-center">
        <div className="col-lg col-md-4">
          <div className="search-boxpost">
            <i className="bi bi-search" />
            <input
              className="form-control filter-pill"
              placeholder="Search by requisition code or title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-auto col-md-4">
          <select className="form-select filter-pill" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
      </div>

      {acting && (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.45)", zIndex: 2000 }}
        >
          <div className="d-flex flex-column align-items-center text-white">
            <span className="spinner-border mb-2" role="status" aria-hidden="true" />
            <span>Processing your request...</span>
          </div>
        </div>
      )}

      {loading ? (
        <div>Loading...</div>
      ) : (
        requisitions.map((req) => {
          const positions = positionsByReq[req.id] || [];
          const departmentCount = new Set(positions.map((p) => p.departmentName)).size;
          const vacancyCount = positions.reduce((sum, p) => sum + (p.vacancies || 0), 0);
          const positionsGroupedByDept = positions.reduce((acc, pos) => {
            const key = pos.departmentName || "Unassigned";
            if (!acc[key]) acc[key] = [];
            acc[key].push(pos);
            return acc;
          }, {});

          return (
            <div className="requisition-card" key={req.id}>
              <div className="d-flex justify-content-between align-items-start" style={{ cursor: "pointer" }} onClick={() => toggleExpand(req.id)}>
                <div className="d-flex align-items-start gap-2 req-left">
                  <input
                    type="checkbox"
                    className="form-check-input mt-1"
                    checked={selected.includes(req.id)}
                    disabled={req.status !== pendingStatus}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(req.id)}
                  />
                  <div className="req-left-body">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="badge bg-light text-dark border">{req.requisitionCode}</span>
                      <span className={`status-pill ${STATUS_PILL[req.status] || "status-pill-secondary"}`}>
                        {req.status === "FULFILLED" ? "Fulfilled" : req.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 req-title-row">
                      <div className="req-code req-title" title={req.title}>{req.title}</div>
                      <button
                        type="button"
                        className="btn btn-link p-0 lh-1 history-icon-btn flex-shrink-0"
                        title="Approval History"
                        onClick={(e) => openApprovalHistory(req, e)}
                      >
                        <i className="bi bi-clock-history" />
                      </button>
                    </div>
                    <div className="req-dates">
                      <span><i className="bi bi-calendar-event" />Start: {formatDate(req.startDate)}</span>
                      <span><i className="bi bi-calendar-check" />Expected Fulfilment: {formatDate(req.expectedFulfilmentDate)}</span>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3 req-right">
                  <div className="req-meta d-none d-md-flex me-3">
                    <span><i className="bi bi-diagram-3" />Departments - {departmentCount}</span>
                    <span><i className="bi bi-briefcase" />Positions - {positions.length}</span>
                    <span><i className="bi bi-people" />Vacancies - {vacancyCount}</span>
                  </div>

                  <button
                    className="icon-btn-circle"
                    title="View Requisition"
                    onClick={(e) => { e.stopPropagation(); navigate("/job-postings/create-requisition", { state: { requisition: req, viewOnly: true, returnTo: "/approvals" } }); }}
                  >
                    <i className="bi bi-eye" />
                  </button>
                  <button className="icon-btn-circle" onClick={(e) => { e.stopPropagation(); toggleExpand(req.id); }}>
                    <i className={`bi bi-chevron-${expanded[req.id] ? "up" : "down"}`} />
                  </button>
                </div>
              </div>

              {expanded[req.id] && (
                <div className="mt-3 pt-3 border-top">
                  {Object.keys(positionsGroupedByDept).length === 0 && (
                    <div className="text-muted">No positions added yet.</div>
                  )}
                  {Object.entries(positionsGroupedByDept).map(([deptName, deptPositions]) => (
                    <div className="department-card" key={deptName}>
                      <div className="department-header d-flex align-items-center gap-2">
                        <i className="bi bi-diagram-3" /> {deptName}
                        <span className="badge bg-white text-dark ms-2">{deptPositions.length} position{deptPositions.length !== 1 ? "s" : ""}</span>
                      </div>
                      {deptPositions.map((pos) => (
                        <div className="position-card-inner" key={pos.id}>
                          <div className="position-row">
                            <div className="position-row-main">
                              <div className="position-title">{pos.positionTitleName}</div>
                              <div className="position-meta-inline">
                                <span><b>Location:</b> {pos.locationName}</span>
                                <span><b>Vacancies:</b> {pos.vacancies}</span>
                                <span><b>Experience:</b> {pos.experienceYears ?? "-"} yrs</span>
                                <span><b>Education:</b> {pos.educationQualificationName || "-"}</span>
                              </div>
                            </div>
                            <div className="position-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="icon-btn-circle"
                                title="View Position"
                                onClick={() => navigate(`/job-postings/${req.id}/add-position`, { state: { position: pos, viewOnly: true, returnTo: "/approvals" } })}
                              >
                                <i className="bi bi-eye" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
      {!loading && requisitions.length === 0 && (
        <div className="text-center text-muted py-5">No requisitions in your approval queue.</div>
      )}

      {!loading && requisitions.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} size={size} onSizeChange={(s) => { setSize(s); setPage(0); }} />
      )}

      {historyModal.show && (
        <div className="modal show d-block" style={{ background: "rgba(0, 0, 0, 0.45)" }} onClick={closeApprovalHistory}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">Approval History</h5>
                  <div className="text-muted fs-13">
                    Track approvals and decisions
                    {historyModal.requisition ? ` — ${historyModal.requisition.requisitionCode}` : ""}
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={closeApprovalHistory} />
              </div>
              <div className="modal-body">
                {historyModal.loading ? (
                  <div className="text-muted">Loading...</div>
                ) : historyModal.rows.length === 0 ? (
                  <div className="text-muted text-center py-4">No approval history yet for this requisition.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle mb-0 approval-history-table">
                      <thead>
                        <tr>
                          <th>Approver</th>
                          <th>Approval Date</th>
                          <th>Status</th>
                          <th>Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyModal.rows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.approverName || "-"}</td>
                            <td>{formatApprovalDateTime(row.approvalDate)}</td>
                            <td>{(row.status || "").replace(/_/g, " ")}</td>
                            <td>{row.comments?.trim() ? row.comments : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeApprovalHistory}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approvals;
